/**
 * ADMIN LICENSE MANAGEMENT DASHBOARD
 *
 * Super admin dashboard for managing licenses across all companies
 */

import { useState } from 'react';
import { createFileRoute, redirect, Link } from '@tanstack/react-router';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Key,
  Plus,
  Search,
  MoreVertical,
  Copy,
  ExternalLink,
  Calendar,
  Building2,
  TrendingUp,
  Loader2,
  MonitorSmartphone,
  Cloud,
  Workflow,
} from 'lucide-react';
import { useAdminLicenses, useAdminLicenseStats } from '@/hooks/queries/useAdminLicenses';
import { CreateLicenseDialog } from '@/components/admin/CreateLicenseDialog';
import type { LicenseFilters } from '@/services/adminLicenseService';

export const Route = createFileRoute('/admin/licenses/')({
  beforeLoad: ({ context }) => {
    if (!context.auth.isAuthenticated) {
      throw redirect({ to: '/login' });
    }

    // Use profile (transformed User from database) instead of user (Supabase auth user)
    const profile = context.auth.profile;

    console.log('Auth profile in beforeLoad:', profile);
    if (!profile?.isSuperAdmin) {
      throw redirect({ to: '/dashboard', replace: true });
    }
  },
  component: AdminLicensesPage,
});

function AdminLicensesPage() {
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [filters, setFilters] = useState<LicenseFilters>({
    status: 'all',
    licenseType: 'all',
    search: '',
  });

  const { data: licenses, isLoading } = useAdminLicenses(filters);
  const { data: stats } = useAdminLicenseStats();

  const copyLicenseKey = (key: string) => {
    navigator.clipboard.writeText(key);
  };

  const getStatusBadge = (license: any) => {
    const now = new Date();
    const expiresAt = new Date(license.expires_at);
    const isExpired = expiresAt <= now;

    if (license.status === 'suspended') {
      return <Badge variant="outline" className="bg-orange-100 text-orange-700 border-orange-300">Suspended</Badge>;
    }

    if (isExpired || license.status === 'expired') {
      return <Badge variant="outline" className="bg-red-100 text-red-700 border-red-300">Expired</Badge>;
    }

    if (license.status === 'trial') {
      return <Badge variant="outline" className="bg-blue-100 text-blue-700 border-blue-300">Trial</Badge>;
    }

    return <Badge variant="outline" className="bg-emerald-100 text-emerald-700 border-emerald-300">Active</Badge>;
  };

  const getLicenseTypeIcon = (type: string) => {
    switch (type) {
      case 'cloud':
        return <Cloud className="w-4 h-4" />;
      case 'desktop':
        return <MonitorSmartphone className="w-4 h-4" />;
      case 'hybrid':
        return <Workflow className="w-4 h-4" />;
      default:
        return <Key className="w-4 h-4" />;
    }
  };

  const getDaysUntilExpiry = (expiresAt: string) => {
    const now = new Date();
    const expiry = new Date(expiresAt);
    const diffTime = expiry.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2">
              <Key className="h-8 w-8" />
              License Management
            </h1>
            <p className="text-muted-foreground mt-2">
              Manage licenses across all companies
            </p>
          </div>
          <Button onClick={() => setCreateDialogOpen(true)} size="lg" className="gap-2">
            <Plus className="w-4 h-4" />
            Create License
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">Total Licenses</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats?.totalLicenses || 0}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">Active</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-emerald-600">{stats?.activeLicenses || 0}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">Expired</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-red-600">{stats?.expiredLicenses || 0}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <TrendingUp className="w-4 h-4" />
                Monthly Revenue
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats?.monthlyRevenue?.toLocaleString('ar-DZ') || 0} DZD</div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search companies or keys..."
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  className="pl-9"
                />
              </div>

              {/* Status Filter */}
              <select
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value as any })}
                className="px-3 py-2 border border-border rounded-md bg-background text-foreground"
              >
                <option value="all">All Statuses</option>
                <option value="active">Active</option>
                <option value="trial">Trial</option>
                <option value="expired">Expired</option>
                <option value="suspended">Suspended</option>
              </select>

              {/* License Type Filter */}
              <select
                value={filters.licenseType}
                onChange={(e) => setFilters({ ...filters, licenseType: e.target.value as any })}
                className="px-3 py-2 border border-border rounded-md bg-background text-foreground"
              >
                <option value="all">All Types</option>
                <option value="cloud">Cloud</option>
                <option value="desktop">Desktop</option>
                <option value="hybrid">Hybrid</option>
              </select>

              {/* Reset Filters */}
              <Button
                variant="outline"
                onClick={() => setFilters({ status: 'all', licenseType: 'all', search: '' })}
              >
                Reset Filters
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Licenses Table */}
        <Card>
          <CardHeader>
            <CardTitle>All Licenses ({licenses?.length || 0})</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
              </div>
            ) : licenses && licenses.length > 0 ? (
              <div className="space-y-3">
                {licenses.map((license) => {
                  const daysUntilExpiry = getDaysUntilExpiry(license.expires_at);

                  return (
                    <Card key={license.id} className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 space-y-3">
                          {/* Company & Plan */}
                          <div className="flex items-center gap-3">
                            <Building2 className="w-5 h-5 text-muted-foreground" />
                            <div>
                              <h3 className="font-semibold">{license.company?.name || 'Unknown Company'}</h3>
                              <p className="text-sm text-muted-foreground">
                                {license.plan?.name} (Tier {license.plan?.tier_level})
                              </p>
                            </div>
                          </div>

                          {/* License Key */}
                          <div className="flex items-center gap-2">
                            <code className="text-sm font-mono bg-muted px-3 py-1 rounded">
                              {license.license_key}
                            </code>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => copyLicenseKey(license.license_key)}
                            >
                              <Copy className="w-3 h-3" />
                            </Button>
                          </div>

                          {/* Metadata */}
                          <div className="flex items-center gap-4 text-sm text-muted-foreground">
                            {/* Type */}
                            <div className="flex items-center gap-1">
                              {getLicenseTypeIcon(license.license_type)}
                              <span className="capitalize">{license.license_type}</span>
                            </div>

                            {/* Status */}
                            {getStatusBadge(license)}

                            {/* Activations */}
                            {license.license_type !== 'cloud' && (
                              <span>
                                {license.activationsCount || 0}/{license.max_devices} devices
                              </span>
                            )}

                            {/* Expiry */}
                            <div className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {daysUntilExpiry > 0 ? (
                                <span className={daysUntilExpiry < 30 ? 'text-orange-600 font-semibold' : ''}>
                                  Expires in {daysUntilExpiry} days
                                </span>
                              ) : (
                                <span className="text-red-600 font-semibold">
                                  Expired {Math.abs(daysUntilExpiry)} days ago
                                </span>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Actions */}
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem asChild>
                              <Link to="/admin/licenses/$licenseId" params={{ licenseId: license.id }}>
                                <ExternalLink className="w-4 h-4 mr-2" />
                                View Details
                              </Link>
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => copyLicenseKey(license.license_key)}>
                              <Copy className="w-4 h-4 mr-2" />
                              Copy Key
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </Card>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-12">
                <Key className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                <h3 className="text-lg font-semibold mb-2">No licenses found</h3>
                <p className="text-muted-foreground mb-4">
                  Create your first license to get started
                </p>
                <Button onClick={() => setCreateDialogOpen(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Create License
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Create License Dialog */}
      <CreateLicenseDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
      />
    </DashboardLayout>
  );
}

export default AdminLicensesPage;
